/**
 * v1.0.8 Batch B — multi-verification entries.
 *
 * VerificationEntriesPanel (staff): manage additional employer/landlord
 * verification entries — add, work status/outcome, internal notes (NTN only),
 * publish/unpublish subscriber findings, attach evidence, cancel/withdraw.
 *
 * VerificationEntriesDisplay (subscriber + staff, read-only): shows the
 * resulting entry structure on the live screening page. Shared findings are
 * on-screen only — they never flow into the final PDF.
 */
import { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, Home, Plus, Lock, Eye, Ban, Paperclip } from "lucide-react";

export const ENTRY_STATUS_LABEL = {
  not_started: "Not Started", in_progress: "In Progress",
  follow_up_required: "Follow-up Required", verified: "Verified",
  partially_verified: "Partially Verified", unable_to_verify: "Unable to Verify",
  discrepancy_identified: "Discrepancy Identified",
};
const STATUS_TONE = {
  verified: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  partially_verified: "bg-lime-50 text-lime-700 ring-lime-200",
  unable_to_verify: "bg-rose-50 text-rose-700 ring-rose-200",
  discrepancy_identified: "bg-amber-50 text-amber-800 ring-amber-200",
  follow_up_required: "bg-amber-50 text-amber-800 ring-amber-200",
  in_progress: "bg-blue-50 text-blue-700 ring-blue-200",
  not_started: "bg-slate-50 text-slate-600 ring-slate-200",
};
const DESIGNATION_LABEL = {
  current: "Current", secondary_current: "Secondary Current",
  previous: "Previous", prior: "Prior", other: "Other",
};
const TRACK_META = {
  employment: { Icon: Briefcase, noun: "employer", title: "Employment Verifications" },
  landlord: { Icon: Home, noun: "landlord / property", title: "Rental History Verifications" },
};

export function EntryStatusBadge({ status, testid }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${STATUS_TONE[status] || STATUS_TONE.not_started}`}
          data-testid={testid}>
      {ENTRY_STATUS_LABEL[status] || status || "Not Started"}
    </span>
  );
}

function useEntries(sid, track) {
  const [entries, setEntries] = useState(null);
  const load = useCallback(() => {
    apiClient.get(`/screenings/${sid}/verification-entries/${track}`)
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]));
  }, [sid, track]);
  useEffect(() => { load(); }, [load]);
  return [entries, load];
}

/* ── Staff management panel ──────────────────────────────────────────── */
function AddEntryDialog({ sid, track, onAdded }) {
  const meta = TRACK_META[track];
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [designation, setDesignation] = useState(track === "employment" ? "secondary_current" : "previous");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/screenings/${sid}/verification-entries/${track}`, { label: label.trim(), designation });
      toast.success(`Added ${meta.noun} verification — worked independently of existing entries`);
      setOpen(false); setLabel("");
      onAdded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add entry");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid={`add-entry-${track}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add {meta.noun}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid={`add-entry-dialog-${track}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Add {meta.noun} verification</DialogTitle>
          <DialogDescription>
            Creates an independent verification entry with its own status, evidence, notes and
            findings. It gates the {track} track until it reaches a terminal state (or is withdrawn).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={label} onChange={(e) => setLabel(e.target.value)}
                 placeholder={track === "employment" ? "Employer / company name" : "Property address or landlord"}
                 data-testid={`add-entry-label-${track}`} />
          <Select value={designation} onValueChange={setDesignation}>
            <SelectTrigger data-testid={`add-entry-designation-${track}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(DESIGNATION_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy || !label.trim()} className="bg-[#0A192F] rounded-sm"
                  data-testid={`add-entry-confirm-${track}`}>
            {busy ? "Adding…" : "Add verification"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffEntryCard({ sid, track, e, documents, onChanged }) {
  const [notes, setNotes] = useState(e.internal_notes || "");
  const [outcome, setOutcome] = useState(e.outcome || "");
  const [finding, setFinding] = useState(e.pm_finding || "");
  const [fileId, setFileId] = useState("");
  const cancelled = !!e.cancelled_at;
  const api = `/screenings/${sid}/verification-entries/${track}/${e.id}`;

  const patch = async (payload, msg) => {
    try {
      await apiClient.patch(api, payload);
      if (msg) toast.success(msg);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); }
  };

  return (
    <div className={`border rounded-sm p-4 space-y-3 ${cancelled ? "border-slate-200 bg-slate-50 opacity-70" : "border-[#0A192F]/15 bg-white"}`}
         data-testid={`entry-card-${e.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-sm text-[#0A192F] truncate" data-testid={`entry-label-${e.id}`}>{e.label}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {DESIGNATION_LABEL[e.designation] || e.designation}
            {cancelled && <span className="ml-2 text-rose-600">Withdrawn by NTN {e.cancel_reason ? `· ${e.cancel_reason}` : ""}</span>}
          </div>
        </div>
        <EntryStatusBadge status={e.status} testid={`entry-status-${e.id}`} />
      </div>

      {!cancelled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Select value={e.status} onValueChange={(v) => patch({ status: v }, `Status → ${ENTRY_STATUS_LABEL[v]}`)}>
              <SelectTrigger data-testid={`entry-status-select-${e.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ENTRY_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input value={outcome} onChange={(ev) => setOutcome(ev.target.value)} placeholder="Outcome summary"
                     data-testid={`entry-outcome-${e.id}`} />
              <Button variant="outline" size="sm" className="rounded-sm shrink-0"
                      onClick={() => patch({ outcome }, "Outcome saved")} data-testid={`entry-outcome-save-${e.id}`}>Save</Button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1">
              <Lock className="h-3 w-3" /> Internal notes · NTN only
            </div>
            <div className="flex gap-2">
              <Textarea rows={2} value={notes} onChange={(ev) => setNotes(ev.target.value)}
                        placeholder="Never shown to the subscriber" data-testid={`entry-notes-${e.id}`} />
              <Button variant="outline" size="sm" className="rounded-sm shrink-0 self-end"
                      onClick={() => patch({ internal_notes: notes }, "Internal note saved")}
                      data-testid={`entry-notes-save-${e.id}`}>Save</Button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1">
              <Eye className="h-3 w-3" /> Subscriber-shared finding · on-screen only, never in the PDF
            </div>
            <Textarea rows={2} value={finding} onChange={(ev) => setFinding(ev.target.value)}
                      placeholder="Narrative of what the evidence showed for THIS entry"
                      data-testid={`entry-finding-${e.id}`} />
            <div className="flex gap-2 mt-1.5">
              <Button size="sm" className="bg-[#0A192F] rounded-sm"
                      disabled={!finding.trim()}
                      onClick={async () => {
                        try {
                          await apiClient.post(`${api}/finding`, { narrative: finding.trim() });
                          toast.success("Finding published to the subscriber (on-screen only)");
                          onChanged();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Publish failed"); }
                      }}
                      data-testid={`entry-finding-publish-${e.id}`}>
                {e.pm_finding_published_at || e.pm_finding ? "Republish" : "Publish"}
              </Button>
              {(e.pm_finding || e.pm_finding_published_at) && (
                <Button size="sm" variant="outline" className="rounded-sm"
                        onClick={async () => {
                          await apiClient.delete(`${api}/finding`);
                          toast.success("Finding unpublished");
                          setFinding("");
                          onChanged();
                        }}
                        data-testid={`entry-finding-unpublish-${e.id}`}>Unpublish</Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Paperclip className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-[11px] text-slate-500">{(e.evidence_file_ids || []).length} evidence file(s)</span>
            <Select value={fileId} onValueChange={setFileId}>
              <SelectTrigger className="w-56 h-8 text-xs" data-testid={`entry-evidence-select-${e.id}`}>
                <SelectValue placeholder="Attach screening file…" />
              </SelectTrigger>
              <SelectContent>
                {(documents || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.filename || d.id.slice(0, 8)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-sm h-8" disabled={!fileId}
                    onClick={async () => {
                      try {
                        await apiClient.post(`${api}/evidence`, { file_id: fileId });
                        toast.success("Evidence attached to this entry");
                        setFileId("");
                        onChanged();
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Attach failed"); }
                    }}
                    data-testid={`entry-evidence-attach-${e.id}`}>Attach</Button>
            <Button size="sm" variant="outline"
                    className="rounded-sm h-8 ml-auto border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={async () => {
                      const reason = window.prompt("Withdraw this verification entry? It stops gating the workflow but keeps full history. Reason (optional):");
                      if (reason === null) return;
                      try {
                        await apiClient.post(`${api}/cancel`, { reason });
                        toast.success("Entry withdrawn — history preserved");
                        onChanged();
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Cancel failed"); }
                    }}
                    data-testid={`entry-cancel-${e.id}`}>
              <Ban className="h-3 w-3 mr-1" /> Withdraw
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function VerificationEntriesPanel({ sid, track }) {
  const meta = TRACK_META[track];
  const [entries, reload] = useEntries(sid, track);
  const [documents, setDocuments] = useState([]);
  useEffect(() => {
    apiClient.get(`/screenings/${sid}`)
      .then(({ data }) => setDocuments(data.documents || []))
      .catch(() => {});
  }, [sid]);
  if (entries === null) return null;
  const primary = entries.find((e) => e.primary);
  const extras = entries.filter((e) => !e.primary);
  return (
    <div className="bg-white rounded-sm border border-[#0A192F]/10 p-5" data-testid={`entries-panel-${track}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <meta.Icon className="h-4 w-4 text-[#0A192F]" />
          <div className="font-serif text-base text-[#0A192F]">{meta.title}</div>
          <span className="text-[11px] text-slate-500">{entries.length} entr{entries.length === 1 ? "y" : "ies"}</span>
        </div>
        <AddEntryDialog sid={sid} track={track} onAdded={reload} />
      </div>
      {primary && (
        <div className="flex items-center justify-between border border-dashed border-[#0A192F]/15 rounded-sm px-3 py-2 mb-3"
             data-testid={`primary-entry-${track}`}>
          <div className="text-sm text-[#0A192F] truncate">
            {primary.label} <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-1">Primary · worked in this workspace</span>
          </div>
          <EntryStatusBadge status={primary.status} />
        </div>
      )}
      <div className="space-y-3">
        {extras.map((e) => (
          <StaffEntryCard key={e.id} sid={sid} track={track} e={e} documents={documents} onChanged={reload} />
        ))}
        {extras.length === 0 && (
          <div className="text-xs text-slate-500">No additional {meta.noun} verifications. Add one when the application package shows more to verify.</div>
        )}
      </div>
    </div>
  );
}

/* ── Subscriber-facing read-only display ─────────────────────────────── */
function DisplayEntry({ e }) {
  const cancelled = !!e.cancelled_at;
  return (
    <div className={`border rounded-sm px-3 py-2.5 ${cancelled ? "border-slate-200 bg-slate-50 opacity-60" : "border-[#0A192F]/10 bg-white"}`}
         data-testid={`display-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-medium text-[#0A192F]">{e.label}</span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-2">
            {DESIGNATION_LABEL[e.designation] || e.designation}
            {cancelled && <span className="ml-2 text-slate-500">Withdrawn by NTN</span>}
          </span>
        </div>
        <EntryStatusBadge status={e.status} testid={`display-entry-status-${e.id}`} />
      </div>
      {e.outcome && !cancelled && <div className="text-xs text-slate-600 mt-1">{e.outcome}</div>}
      {e.pm_finding && !cancelled && (
        <div className="mt-2 border-l-2 border-[#0A192F] bg-[#F3EFEA]/50 px-3 py-2 rounded-r-sm"
             data-testid={`display-entry-finding-${e.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">NTN Findings</div>
          <div className="text-sm text-[#0A192F] whitespace-pre-wrap">{e.pm_finding}</div>
        </div>
      )}
    </div>
  );
}

export function VerificationEntriesDisplay({ sid }) {
  const [emp] = useEntries(sid, "employment");
  const [lan] = useEntries(sid, "landlord");
  if (emp === null || lan === null) return null;
  const interesting = (list) => list.length > 1 || list.some((e) => e.pm_finding || e.cancelled_at);
  const showEmp = interesting(emp);
  const showLan = interesting(lan);
  if (!showEmp && !showLan) return null;
  return (
    <div className="bg-white rounded-sm border border-[#0A192F]/10 p-5 space-y-4" data-testid="verification-entries-display">
      {showEmp && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="h-4 w-4 text-[#0A192F]" />
            <div className="font-serif text-base text-[#0A192F]">Employment Verifications</div>
          </div>
          <div className="space-y-2">{emp.map((e) => <DisplayEntry key={e.id} e={e} />)}</div>
        </div>
      )}
      {showLan && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Home className="h-4 w-4 text-[#0A192F]" />
            <div className="font-serif text-base text-[#0A192F]">Rental History Verifications</div>
          </div>
          <div className="space-y-2">{lan.map((e) => <DisplayEntry key={e.id} e={e} />)}</div>
        </div>
      )}
      <p className="text-[10px] text-slate-400">
        NTN findings shown here are on-screen context from NTN staff and are not part of the final Screening Summary PDF.
      </p>
    </div>
  );
}
